import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { getAvailableGeographyLayers, getManagedLayerGeoJson } from '../../services/geographyService';
import TraditionalLandsLayer from './TraditionalLandsLayer';
import { isTerritoryLayer } from '../../utils/territoryLayers';
import { getTraditionalLandPolygons } from '../../services/stewardshipService';

export default function HarvestTerritoryLayer({ tenantId, interactive = true, fitOnLoad = false, children, controlsContainer }) {
  const map = useMap();
  const fitted = useRef(null);
  const [state, setState] = useState(null);
  const [revision, setRevision] = useState(0);
  const [hiddenTenant, setHiddenTenant] = useState(null);
  const visible = !hiddenTenant || hiddenTenant.tenantId !== tenantId;
  useEffect(() => {
    let current = true;
    setState({ tenantId, loading: true, polygons: [] });
    const load = async () => {
      try {
        const context = { ...(tenantId ? { tenantId } : {}), module: 'harvesting' };
        const sources = await Promise.allSettled([
          tenantId ? getTraditionalLandPolygons(tenantId).then((response) => response.data) : Promise.resolve([]),
          getAvailableGeographyLayers(context).then(async (catalog) => {
            const entries = await Promise.all(catalog.data.filter(isTerritoryLayer).map(async (layer) => {
              const response = await getManagedLayerGeoJson(layer._id, { ...context, limit: 3000 });
              if (response.data.truncated) throw new Error(`${layer.displayName}: the territory is incomplete. Staff must check the published layer.`);
              return response.data.features.map((feature, index) => ({
                _id: `${layer._id}-${feature.id || index}`,
                name: feature.properties?.name || layer.displayName,
                geometry: feature.geometry,
              }));
            }));
            return entries.flat();
          }),
        ]);
        const seen = new Set();
        const polygons = sources.filter((source) => source.status === 'fulfilled')
          .flatMap((source) => source.value).filter((polygon) => {
            if (!['Polygon', 'MultiPolygon'].includes(polygon.geometry?.type) || !polygon.geometry?.coordinates?.length) return false;
            const key = JSON.stringify(polygon.geometry);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        const errors = sources.filter((source) => source.status === 'rejected')
          .map((source) => source.reason.response?.data?.message || source.reason.message || 'Could not load a territory source.');
        if (current) setState({ tenantId, polygons, loading: false, error: errors.join(' ') });
      } catch (error) {
        if (current) setState({ tenantId, polygons: [], loading: false,
          error: error.response?.data?.message || error.message || 'Could not load the territory boundary.' });
      }
    };
    load();
    return () => { current = false; };
  }, [tenantId, revision]);

  useEffect(() => {
    if (!visible || !fitOnLoad || state?.tenantId !== tenantId || !state?.polygons.length
      || (fitted.current && fitted.current.tenantId === tenantId)) return;
    const bounds = L.geoJSON({
      type: 'FeatureCollection',
      features: state.polygons.map((polygon) => ({ type: 'Feature', properties: {}, geometry: polygon.geometry })),
    }).getBounds();
    if (bounds.isValid()) {
      map.fitBounds(bounds, { maxZoom: 9, padding: [20, 20] });
      fitted.current = { tenantId };
    }
  }, [visible, fitOnLoad, map, state, tenantId]);

  if (!state || state.tenantId !== tenantId) return null;
  const controls = <div className={controlsContainer ? 'harvest-map-flow__layers' : 'harvest-territory-status'} ref={(element) => {
      if (element) L.DomEvent.disableClickPropagation(element);
    }} onClick={(event) => event.stopPropagation()}>
      {state.loading ? <span role="status">Loading territory boundary...</span>
        : state.error ? <span role="alert">{state.polygons.length ? 'Some territory sources unavailable: ' : 'Territory boundary unavailable: '}{state.error} <button type="button" onClick={() => setRevision((value) => value + 1)}>Retry territory</button></span>
          : !state.polygons.length && <span>No published territory boundary is available for this view.</span>}
      {state.polygons.length > 0 && <button type="button" aria-pressed={visible}
        title="Territory boundary - reference only, not harvesting permission."
        onClick={() => setHiddenTenant(visible ? { tenantId } : null)}>
        {visible ? 'Hide territory' : 'Show territory'}
      </button>}
      {children}
    </div>;
  return <>
    {visible && <TraditionalLandsLayer polygons={state.polygons} interactive={interactive} />}
    {controlsContainer ? createPortal(controls, controlsContainer) : controls}
  </>;
}
